import { useEffect, useId, useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type Bid, type BidPatchFields } from '../lib/api'
import { normalizeThorId } from '../lib/thor'
import { useBidProfile } from '../lib/bid-profile'
import { ThorIdField, type ThorHit } from './ThorIdField'

interface Draft {
  thorId: string
  client: string
  name: string
}

const draftOf = (b: Bid | undefined): Draft => ({
  thorId: b?.thorId ?? '',
  client: b?.client ?? '',
  name: b?.name ?? '',
})

/** One change Thor would make, as the preview shows it: "Client: A → B". */
interface Change {
  from: string
  to: string
}

/** What a Thor hit would change — only the fields where Thor differs from the form (null = same). */
interface Preview {
  client: Change | null
  name: Change | null
}

/**
 * The Bid Settings page's first block: the bid's Thor ID, client and name, in that order. The
 * client prefixes the bid's label everywhere ("Client - Bid name", lib/api.ts#bidDisplayLabel).
 *
 * The bid name is its DISPLAY name (which is the opportunity's name): it can be changed here, never
 * blanked (Save is disabled while it is), and renaming never changes the bid's id — the URL, rooms,
 * artefacts and index stay keyed by it, so this page stays where it is after a Save.
 *
 * Reads the bid out of the `['bids']` cache the landing, breadcrumb and dashboards already share —
 * no fetch of its own — and Save invalidates that same query, which is what updates all of them
 * without a reload. The Thor ID and client are optional: emptying one and saving clears it.
 *
 * A Thor hit never overwrites silently here (unlike the new-bid form, nothing is blank to fill):
 * when Thor's client or opportunity name differs from the form's Client or Bid name, a preview
 * says "Client: A → B · Bid name: C → D" (only the lines that change), and the member applies or
 * ignores it — Dealtracker's ThorUpdatePanel pattern. Apply only fills the fields; Save persists.
 * Clearing the Thor ID leaves the client and the name as they are.
 *
 * Everyone on the bid sees the card; only an editor/admin can change it — the same bar as
 * BrandingCard, not IndexLimitCard's tenant-admin one. The server refuses a viewer regardless.
 */
export function BidDetailsCard({ bid }: { bid: string }) {
  const { t } = useLang()
  const qc = useQueryClient()
  const nameHintId = useId()
  const { canEdit } = useBidProfile(bid)
  const bids = useQuery({ queryKey: ['bids'], queryFn: api.bids })
  const record = bids.data?.bids.find((b) => b.id === bid)

  const [draft, setDraft] = useState<Draft>(() => draftOf(record))
  const [preview, setPreview] = useState<Preview | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  // (Re)seed from the fetched record: on first load, after Save's refetch, or when another member
  // changed it. Keyed on the VALUES, so a refetch that changes nothing keeps unsaved edits.
  const stored = draftOf(record)
  const { thorId: storedThorId, client: storedClient, name: storedName } = stored
  useEffect(() => {
    setDraft({ thorId: storedThorId, client: storedClient, name: storedName })
    setPreview(null)
  }, [bid, storedThorId, storedClient, storedName])

  const canonicalThorId = normalizeThorId(draft.thorId)
  const thorIdOk = draft.thorId.trim() === '' || canonicalThorId !== null
  const nameOk = draft.name.trim() !== ''
  const patch = changesOf(stored, draft)
  // A blanked name is a change too (Reset must undo it), though not one Save can send.
  const dirty = Object.keys(patch).length > 0 || draft.name.trim() !== stored.name
  const duplicate =
    canonicalThorId !== null &&
    (bids.data?.bids ?? []).some((b) => b.id !== bid && b.thorId === canonicalThorId)
      ? t('thor_duplicate')
      : null

  const set = (key: keyof Draft, value: string): void => {
    setDraft((d) => ({ ...d, [key]: value }))
    setSaved(false)
  }

  function onThorHit(hit: ThorHit): void {
    // `hit.opportunity` is Thor's opportunity name — the bid name. A name Thor does not have (null)
    // is no change.
    const change = (current: string, next: string | null): Change | null =>
      next && next !== current.trim() ? { from: current.trim(), to: next } : null
    const client = change(draft.client, hit.client)
    const name = change(draft.name, hit.opportunity)
    setPreview(client || name ? { client, name } : null)
  }

  function applyPreview(): void {
    if (!preview) return
    const { client, name } = preview
    setDraft((d) => ({
      ...d,
      ...(client ? { client: client.to } : {}),
      ...(name ? { name: name.to } : {}),
    }))
    setPreview(null)
    setSaved(false)
  }

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!dirty || !thorIdOk || !nameOk || saving) return
    setSaving(true)
    setError('')
    try {
      await api.updateBidMetadata(bid, patch)
      await qc.invalidateQueries({ queryKey: ['bids'] })
      setSaved(true)
    } catch {
      setError(t('bid_details_save_failed'))
    } finally {
      setSaving(false)
    }
  }

  function reset(): void {
    setDraft(stored)
    setPreview(null)
    setError('')
    setSaved(false)
  }

  const shown = (v: string): string => v || '—'
  const previewLines = preview
    ? [
        preview.client && `${t('bid_client')}: ${shown(preview.client.from)} → ${preview.client.to}`,
        preview.name && `${t('bid_name')}: ${shown(preview.name.from)} → ${preview.name.to}`,
      ].filter(Boolean)
    : []

  return (
    <section className="details-card" aria-label={t('bid_details_title')}>
      <div className="details-head">
        <span className="home-eyebrow">{t('bid_details_title')}</span>
      </div>
      <p className="brand-intro">{t('bid_details_intro')}</p>
      {bids.isLoading ? (
        <p aria-live="polite">…</p>
      ) : (
        <form onSubmit={(e) => void save(e)}>
          <div className="details-fields">
            <ThorIdField
              value={draft.thorId}
              onChange={(v) => {
                set('thorId', v)
                // A preview belongs to the number it was looked up for.
                setPreview(null)
              }}
              onFound={onThorHit}
              label={t('bid_thor_id')}
              disabled={!canEdit}
              warning={duplicate}
            />
            <label className="prompt-label">
              {t('bid_client')}
              <input
                className="prompt-input"
                value={draft.client}
                disabled={!canEdit}
                autoComplete="off"
                onChange={(e) => set('client', e.target.value)}
              />
            </label>
            <div className="details-wide">
              <label className="prompt-label">
                {t('bid_name')}
                <input
                  className="prompt-input"
                  value={draft.name}
                  required
                  disabled={!canEdit}
                  autoComplete="off"
                  aria-invalid={!nameOk || undefined}
                  aria-describedby={nameOk ? undefined : nameHintId}
                  onChange={(e) => set('name', e.target.value)}
                />
              </label>
              {canEdit && !nameOk && (
                <p id={nameHintId} className="thor-status thor-status-error">
                  {t('bid_name_required')}
                </p>
              )}
            </div>
          </div>

          {preview && (
            <div className="details-preview" role="group" aria-label={t('thor_preview_title')}>
              <p>
                {t('thor_preview_title')} {previewLines.join(' · ')}
              </p>
              <div className="details-preview-acts">
                <button type="button" className="mini ok" onClick={applyPreview}>
                  {t('thor_apply')}
                </button>
                <button type="button" className="mini ghost" onClick={() => setPreview(null)}>
                  {t('thor_ignore')}
                </button>
              </div>
            </div>
          )}

          {error && (
            <p className="details-error" role="alert">
              {error}
            </p>
          )}

          {canEdit && (
            <div className="details-actions">
              <span className="details-saved" role="status" aria-live="polite">
                {saved && !dirty ? t('bid_details_saved') : ''}
              </span>
              <button type="button" className="mini" onClick={reset} disabled={saving || !dirty}>
                {t('bid_details_reset')}
              </button>
              <button type="submit" className="mini ok" disabled={saving || !dirty || !thorIdOk || !nameOk}>
                {t('save')}
              </button>
            </div>
          )}
        </form>
      )}
    </section>
  )
}

/**
 * Only the fields the member changed, in the PATCH's terms: `''` clears the Thor ID or client, the
 * Thor ID goes out canonical, and the name goes out trimmed (never blank — Save is disabled then).
 * Sending just these leaves a field someone else changed meanwhile alone.
 */
function changesOf(stored: Draft, draft: Draft): BidPatchFields {
  const out: BidPatchFields = {}
  const client = draft.client.trim()
  const thorId = draft.thorId.trim() === '' ? '' : (normalizeThorId(draft.thorId) ?? draft.thorId.trim())
  const name = draft.name.trim()
  if (client !== stored.client) out.client = client
  if (thorId !== stored.thorId) out.thorId = thorId
  if (name !== '' && name !== stored.name) out.name = name
  return out
}
